// Friendly, deterministic session names (Brief 4 polish). The bare
// "Session #<id>" is forgettable; this gives each session a stable
// adjective-noun handle in the "orange-turkey" style (e.g. "tidal-otter") —
// nautical-themed to match the brand. The numeric id stays the precise
// disambiguator (the call sites keep it in a tooltip / aria-label).
//
// PURE + DETERMINISTIC + TOTAL: the same thread id ALWAYS yields the same name,
// with no storage, no clock and no randomness, so a session reads identically on
// every surface (web, desktop, radio) and across reloads. No dependency — the
// word lists are vendored here so `shared/` ports verbatim.

/** Calm, mostly-nautical adjectives (the colour/quality half of the handle). */
const ADJECTIVES = [
  "tidal",
  "coral",
  "azure",
  "drifting",
  "misty",
  "briny",
  "cresting",
  "anchored",
  "pearly",
  "marine",
  "breezy",
  "sunlit",
  "glassy",
  "windward",
  "leeward",
  "teal",
  "dawnlit",
  "dusky",
  "jade",
  "silver",
  "stormy",
  "foaming",
  "saltworn",
  "gilded",
];

/** Sea creatures + nautical nouns (the "turkey" half of the handle). */
const NOUNS = [
  "otter",
  "marlin",
  "heron",
  "dolphin",
  "narwhal",
  "puffin",
  "tern",
  "walrus",
  "seal",
  "manta",
  "urchin",
  "conch",
  "signal",
  "lantern",
  "compass",
  "anchor",
  "schooner",
  "skipper",
  "mariner",
  "current",
  "breaker",
  "harbor",
  "dory",
  "albatross",
];

/** A small, well-mixed hash of an id → uint32 (Knuth's multiplicative hash), so
 * adjacent ids land far apart and neighbours don't share an adjective or noun.
 * Total: a non-finite or negative id folds to 0. */
function hashId(id: number): number {
  const n = Number.isFinite(id) ? Math.abs(Math.trunc(id)) : 0;
  return Math.imul(n, 2654435761) >>> 0;
}

/** The stable friendly name for a session thread id, e.g. 42 → "tidal-otter".
 * Deterministic and total. With 24×24 = 576 combinations, collisions are rare
 * at real session counts; the id remains the exact key everywhere. */
export function sessionName(id: number): string {
  const h = hashId(id);
  const adjective = ADJECTIVES[h % ADJECTIVES.length];
  const noun = NOUNS[Math.floor(h / ADJECTIVES.length) % NOUNS.length];
  return `${adjective}-${noun}`;
}

/** Shorten a model id for display: strip the vendor prefix and any trailing
 * date stamp. "claude-opus-4-8" → "opus-4-8", "claude-haiku-4-5-20251001" →
 * "haiku-4-5". An unknown shape passes through trimmed. */
export function shortModel(model: string): string {
  return model
    .trim()
    .replace(/^claude-/, "")
    .replace(/-\d{8}$/, "");
}

/** The session's concrete-identity label (Brief 4 — device-harness-model). Joins
 * the KNOWN parts — machine (device), harness, short model — with " · ", e.g.
 * "studio · claudecode · opus-4-8". Falls back to the stable nautical handle
 * ([`sessionName`]) when none are known yet (an older session, or before the
 * bridge has named the agent). The numeric thread id stays the exact key the
 * call sites keep in a tooltip / aria-label. */
export function sessionLabel(s: {
  thread_id: number;
  machine_id?: string;
  harness?: string;
  model?: string;
}): string {
  const parts = [
    s.machine_id?.trim(),
    s.harness?.trim(),
    s.model ? shortModel(s.model) : undefined,
  ].filter((p): p is string => !!p && p.length > 0);
  return parts.length > 0 ? parts.join(" · ") : sessionName(s.thread_id);
}
